import { Link, useLocation } from "react-router-dom";
import { Compass } from "lucide-react";

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useSEO } from "@/lib/useSEO";

/**
 * Catch-all 404.
 *
 * Sep 2026: the router had no `path="*"`, so any unmatched URL rendered
 * an EMPTY page — which is exactly how the partner notification bell's
 * broken link ("/partner/quote-leads", an API path) surfaced as a blank
 * screen. A mistyped or retired link should always land somewhere
 * useful instead.
 */
export default function NotFound() {
  const { pathname } = useLocation();
  useSEO({
    title: "Page not found | AWARE Water Testing",
    description: "That page could not be found. Browse water testing packages, locations and guides.",
    noindex: true,
  });

  const LINKS = [
    { to: "/#water-tests", label: "Water testing packages" },
    { to: "/book-water-test", label: "Book a water test" },
    { to: "/locations", label: "Locations we cover" },
    { to: "/knowledge-hub", label: "Knowledge Hub" },
    { to: "/contact", label: "Contact us" },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="not-found-page">
      <Header />
      <main className="pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">
            <Compass className="w-3.5 h-3.5" /> 404
          </div>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">
            We couldn&rsquo;t find that page
          </h1>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            The link may be out of date or mistyped. Nothing is wrong with your
            account or your results &mdash; try one of these instead.
          </p>
          <code
            className="mt-4 inline-block rounded-lg bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs text-slate-500"
            data-testid="not-found-path"
          >
            {pathname}
          </code>

          <ul className="mt-8 grid sm:grid-cols-2 gap-3">
            {LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`not-found-link-${l.to.replace(/\//g, "")}`}
                  className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 hover:border-cyan-300 hover:text-cyan-800 transition-colors"
                >
                  {l.label}
                  <span aria-hidden>&rarr;</span>
                </Link>
              </li>
            ))}
          </ul>

          <p className="mt-8 text-sm text-slate-500">
            Signed in as a partner?{" "}
            <Link to="/partner" className="font-semibold text-cyan-700 hover:text-cyan-800 underline underline-offset-2" data-testid="not-found-partner-link">
              Return to the Partner Portal
            </Link>
            .
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
